import { decodeJwt } from "jose";
import { createContext, ReactNode, useState } from "react";
import { useBrowserLocalStorage } from "../hooks/useBrowserLocalStorage";
import { useSearchParams } from "../hooks/useSearchParams";
import { JWTUserAuthenticator } from "src/contexts/auth/domain/JWTUserAuthenticator";

export type AuthContextType = {
  token: string;
  setToken: (token: string) => void;
  isLoginlessAccessToken: boolean;
  loginEmail: string;
  setLoginEmail: (email: string) => void;
  otpSubmitError: string;
  setOTPSubmitError: (error: string) => void;
  emailSubmitError: string;
  setEmailSubmitError: (error: string) => void;
  deleteToken: () => void;
};

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { linkType, slug, token: searchParamsToken, isUserLink } = useSearchParams();

  const [storedToken, setStoredToken] = useBrowserLocalStorage<string | null>(
    `stored_query_param.token.${linkType}.${slug}`,
    null,
  );

  const isUserAuthenticatedByCustomer = (token: string) => {
    if (!token) return false;

    const payload = decodeJwt(token);

    return payload["https://bankflip.io/authenticator"] == JWTUserAuthenticator.CUSTOMER;
  };

  const initialState = (searchParamsToken ? searchParamsToken : storedToken) ?? "";
  const [token, setToken] = useState(initialState);
  const isLoginlessAccessToken = isUserAuthenticatedByCustomer(token);

  const [loginEmail, setLoginEmail] = useState("");
  const [otpSubmitError, setOTPSubmitError] = useState("");
  const [emailSubmitError, setEmailSubmitError] = useState("");

  const deleteToken = () => {
    setToken("");
    if (!isUserLink) {
      setLoginEmail("");
    }
    setStoredToken(null);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        isLoginlessAccessToken,
        loginEmail,
        setLoginEmail,
        otpSubmitError,
        setOTPSubmitError,
        emailSubmitError,
        setEmailSubmitError,
        deleteToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
